import {
  FRAME_CALIBRATION_FILTERS,
  FRAME_CHANGE_FILTERS,
  type FrameCalibrationFilter,
  type FrameChangeFilter,
  type FrameReviewQueueQuery,
} from '@/api/admin';

/**
 * Filters of the frame-review walkthrough. They live in the URL next to
 * the `sku`/`mockup` link params, so a copied link opens the same queue.
 * Defaults are not written, which keeps plain links short.
 */

export const FRAME_REVIEW_FILTER_PARAMS = {
  status: 'status',
  changed: 'changed',
  days: 'days',
  calibration: 'calibration',
  search: 'q',
} as const;

export type FrameReviewStatusFilter = 'active' | 'inactive' | 'all';
export const FRAME_REVIEW_STATUS_FILTERS: FrameReviewStatusFilter[] = [
  'active',
  'inactive',
  'all',
];

export interface FrameReviewFilters {
  status: FrameReviewStatusFilter;
  changed: 'any' | FrameChangeFilter;
  /** Day window of the `within`/`before` change filters. */
  days: number;
  calibration: 'any' | FrameCalibrationFilter;
  search: string;
}

/**
 * Deactivated products rarely need a frame check, so the queue starts
 * active, and it starts on the open work: the templates still to calibrate
 * (`open`). `any` steps through every template again.
 */
export const DEFAULT_FRAME_REVIEW_FILTERS: FrameReviewFilters = {
  status: 'active',
  changed: 'any',
  days: 7,
  calibration: 'open',
  search: '',
};

/** The calibration choices of the walkthrough, `any` included. */
const CALIBRATION_CHOICES: ReadonlyArray<FrameReviewFilters['calibration']> = [
  'any',
  ...FRAME_CALIBRATION_FILTERS,
];

const MAX_DAYS = 3650;

/** A typed or URL day window counts when it is a whole number in 1…3650. */
export function isValidFrameReviewDays(value: string | null): value is string {
  if (!value || !/^\d+$/.test(value)) return false;
  const days = Number(value);
  return days >= 1 && days <= MAX_DAYS;
}

/** The day window from the URL; anything invalid falls back to the default. */
export function parseFrameReviewDays(value: string | null): number {
  return isValidFrameReviewDays(value)
    ? Number(value)
    : DEFAULT_FRAME_REVIEW_FILTERS.days;
}

export function parseFrameReviewFilters(
  params: URLSearchParams
): FrameReviewFilters {
  const P = FRAME_REVIEW_FILTER_PARAMS;
  const status = params.get(P.status);
  const changed = params.get(P.changed);
  const calibration = params.get(P.calibration);
  return {
    status:
      FRAME_REVIEW_STATUS_FILTERS.find((s) => s === status) ??
      DEFAULT_FRAME_REVIEW_FILTERS.status,
    changed:
      FRAME_CHANGE_FILTERS.find((c) => c === changed) ??
      DEFAULT_FRAME_REVIEW_FILTERS.changed,
    days: parseFrameReviewDays(params.get(P.days)),
    calibration:
      CALIBRATION_CHOICES.find((c) => c === calibration) ??
      DEFAULT_FRAME_REVIEW_FILTERS.calibration,
    search: params.get(P.search)?.trim() ?? '',
  };
}

/**
 * Writes the filters into a copy of the params. Defaults are removed, the
 * other params (the `sku`/`mockup` link) stay as they are.
 */
export function writeFrameReviewFilters(
  params: URLSearchParams,
  filters: FrameReviewFilters
): URLSearchParams {
  const P = FRAME_REVIEW_FILTER_PARAMS;
  const D = DEFAULT_FRAME_REVIEW_FILTERS;
  const next = new URLSearchParams(params);
  const put = (name: string, value: string, isDefault: boolean) => {
    if (isDefault) next.delete(name);
    else next.set(name, value);
  };
  put(P.status, filters.status, filters.status === D.status);
  put(P.changed, filters.changed, filters.changed === D.changed);
  // The window only matters together with a change filter.
  put(
    P.days,
    String(filters.days),
    filters.days === D.days || filters.changed === 'any'
  );
  put(
    P.calibration,
    filters.calibration,
    filters.calibration === D.calibration
  );
  const search = filters.search.trim();
  put(P.search, search, search === '');
  return next;
}

/** The API query behind the filters. */
export function toFrameReviewQuery(
  filters: FrameReviewFilters
): FrameReviewQueueQuery {
  return {
    ...(filters.status !== 'all' && { isActive: filters.status === 'active' }),
    ...(filters.search.trim() && { search: filters.search.trim() }),
    ...(filters.changed !== 'any' && {
      changed: filters.changed,
      days: filters.days,
    }),
    ...(filters.calibration !== 'any' && { calibration: filters.calibration }),
  };
}

/** True when any filter differs from its default. */
export function hasActiveFrameReviewFilters(
  filters: FrameReviewFilters
): boolean {
  const D = DEFAULT_FRAME_REVIEW_FILTERS;
  return (
    filters.status !== D.status ||
    filters.changed !== D.changed ||
    filters.calibration !== D.calibration ||
    filters.search.trim() !== ''
  );
}
